import { adminState, adminAction, logout, sendInvites, inviteLinks, type AdminState, type InviteResult } from "../api";
import { esc as escapeHtml } from "./util";

// Gamemaster console. Build teams + people manually (email optional); send
// magic-link invites whenever you like; drag people between teams (their login
// is unaffected). Email/invites are entirely optional.
export function mountAdmin(root: HTMLElement, onOpenGame: (gameId: string, name: string) => void) {
  let state: AdminState = { games: [], teams: [], players: [] };
  let links: Record<string, InviteResult> = {};
  let selected: string | null = null;
  let banner = "";

  async function refresh() {
    state = await adminState();
    if (!selected && state.games.length) selected = state.games[0].id;
    links = {};
    if (selected) {
      try { (await inviteLinks(selected)).links.forEach((l) => (links[l.id] = l)); } catch { /* ignore */ }
    }
    render();
  }
  async function act(action: string, params: Record<string, unknown>): Promise<unknown> {
    try { const r = await adminAction(action, params); await refresh(); return r; }
    catch (e) { alert((e as Error).message); }
  }

  async function copyLink(playerId: string) {
    const link = links[playerId]?.link;
    if (!link) return;
    try { await navigator.clipboard.writeText(link); banner = "✓ Invite link copied to clipboard."; }
    catch { banner = link; }
    render();
  }
  async function send(ids?: string[]) {
    try {
      const { results } = await sendInvites(selected!, ids);
      const emailed = results.filter((r) => r.emailed).length;
      const noEmail = results.filter((r) => !r.email).length;
      banner = `Sent ${emailed} email${emailed === 1 ? "" : "s"}.` + (noEmail ? ` ${noEmail} person(s) have no email — use Copy link for them.` : "");
      render();
    } catch (e) { alert((e as Error).message); }
  }

  function render() {
    const game = state.games.find((g) => g.id === selected) || null;
    const teams = state.teams.filter((t) => t.gameId === selected);
    const players = state.players.filter((p) => p.gameId === selected);
    const group = game?.mode === "group";

    const personRow = (p: AdminState["players"][number]) => {
      const hasEmail = !!p.email;
      return `<li class="person" draggable="true" data-player="${p.id}">
        <span class="grip">⠿</span>
        <span class="p-name">${p.displayName}</span>
        <span class="p-email muted">${p.email || "no email"}</span>
        <span class="p-actions">
          <button class="link-btn" data-copy="${p.id}" title="Copy magic login link">🔗</button>
          ${hasEmail ? `<button class="link-btn" data-send="${p.id}" title="Email invite">✉</button>` : ""}
          <button class="x" data-delplayer="${p.id}">×</button>
        </span>
      </li>`;
    };

    root.innerHTML = `
      <div class="admin">
        <header class="admin-head">
          <h1>Gamemaster</h1>
          <div><button class="ghost" id="chpw">Change password</button> <button class="ghost" id="logout">Log out</button></div>
        </header>
        ${banner ? `<div class="banner">${escapeHtml(banner)} <button id="banner-x">×</button></div>` : ""}
        <div class="admin-grid">
          <section class="card">
            <h2>Games</h2>
            <ul class="adm-list">${state.games.map((g) => `<li class="${g.id === selected ? "sel" : ""}"><button class="pick" data-game="${g.id}">${g.name} <em class="role">${g.mode}</em></button><button class="x" data-delgame="${g.id}">×</button></li>`).join("") || `<li class="muted">No games yet.</li>`}</ul>
            <form class="row" id="new-game"><input id="game-name" placeholder="New game name" required/><select id="game-mode"><option value="seekingteams">seeking teams</option><option value="group">group seeking</option></select><button class="primary">Create</button></form>
          </section>

          ${game ? `
          <section class="card">
            <div class="card-head"><h2>${game.name}</h2><div><label class="mode-toggle">Mode <select id="mode-sel"><option value="seekingteams" ${group ? "" : "selected"}>Seeking teams</option><option value="group" ${group ? "selected" : ""}>Group seeking</option></select></label> <button class="primary sm" id="open-map">Open map →</button></div></div>

            ${group ? `<p class="hint">Group seeking: everyone shares one seeker board. Designate who's hiding on the map screen (Open map → <b>Hiders</b>). No teams needed.</p>` : `
            <h3>Teams <span class="count">${teams.length}</span></h3>
            <ul class="adm-list">${teams.map((t) => `<li><span>${t.name} <em class="role role-${t.role}">${t.role}</em> · ${players.filter((p) => p.teamId === t.id).length}</span><button class="x" data-delteam="${t.id}">×</button></li>`).join("") || `<li class="muted">No teams.</li>`}</ul>
            <form class="row" id="new-team"><input id="team-name" placeholder="Team name" required/><select id="team-role"><option value="seeker">seeker</option><option value="hider">hider</option></select><button class="primary">＋ Add team</button></form>`}

            <div class="people-head"><h3>${group ? "People" : "People by team"} <span class="count">${players.length}</span></h3>
              <button class="ghost sm" id="send-all">✉ Send all invites</button></div>

            ${group
              ? `<ul class="person-list">${players.map(personRow).join("") || `<li class="muted">No one yet.</li>`}</ul>`
              : (teams.map((t) => `<div class="team-group drop" data-team="${t.id}"><div class="tg-head">${t.name} <em class="role role-${t.role}">${t.role}</em></div><ul class="person-list">${players.filter((p) => p.teamId === t.id).map(personRow).join("") || `<li class="muted drop-hint">Drag people here, or add below.</li>`}</ul></div>`).join("") || `<li class="muted">Add a team first.</li>`)}

            <form class="row wrap" id="add-person">
              <input id="i-display" placeholder="Full name" required/>
              <input id="i-email" type="email" placeholder="email (optional)"/>
              ${group ? "" : `<select id="i-team" required>${teams.map((t) => `<option value="${t.id}">${t.name}</option>`).join("")}</select>`}
              <button class="primary" ${group || teams.length ? "" : "disabled"}>＋ Add person</button>
            </form>
            <p class="hint">Add people with just a name — email is optional. Send magic-link invites whenever you're ready (🔗 copies a link, ✉ emails it). Moving someone never changes their link.</p>
          </section>` : `<section class="card muted">Select or create a game.</section>`}
        </div>
      </div>`;

    root.querySelector("#logout")!.addEventListener("click", () => logout());
    root.querySelector("#banner-x")?.addEventListener("click", () => { banner = ""; render(); });
    root.querySelector("#chpw")!.addEventListener("click", async () => {
      const oldPassword = prompt("Current password"); if (!oldPassword) return;
      const newPassword = prompt("New password (min 6 chars)"); if (!newPassword) return;
      const r = await act("changePassword", { oldPassword, newPassword });
      if (r) { banner = "✓ Password changed."; render(); }
    });
    root.querySelectorAll<HTMLButtonElement>("[data-game]").forEach((b) => b.addEventListener("click", () => { selected = b.dataset.game!; banner = ""; refresh(); }));
    root.querySelectorAll<HTMLButtonElement>("[data-delgame]").forEach((b) => b.addEventListener("click", () => { if (confirm("Delete this game and everyone in it?")) act("deleteGame", { id: b.dataset.delgame }); }));
    root.querySelector<HTMLFormElement>("#new-game")?.addEventListener("submit", (e) => { e.preventDefault(); act("createGame", { name: (root.querySelector<HTMLInputElement>("#game-name")!).value, mode: (root.querySelector<HTMLSelectElement>("#game-mode")!).value }); });
    root.querySelector("#open-map")?.addEventListener("click", () => game && onOpenGame(game.id, game.name));
    root.querySelector<HTMLSelectElement>("#mode-sel")?.addEventListener("change", (e) => act("setGameMode", { id: game!.id, mode: (e.target as HTMLSelectElement).value }));

    root.querySelectorAll<HTMLButtonElement>("[data-delteam]").forEach((b) => b.addEventListener("click", () => { if (confirm("Delete team and its players?")) act("deleteTeam", { id: b.dataset.delteam }); }));
    root.querySelector<HTMLFormElement>("#new-team")?.addEventListener("submit", (e) => { e.preventDefault(); act("createTeam", { gameId: selected, name: (root.querySelector<HTMLInputElement>("#team-name")!).value, role: (root.querySelector<HTMLSelectElement>("#team-role")!).value }); });

    root.querySelectorAll<HTMLButtonElement>("[data-delplayer]").forEach((b) => b.addEventListener("click", () => { if (confirm("Delete this person?")) act("deletePlayer", { id: b.dataset.delplayer }); }));
    root.querySelectorAll<HTMLButtonElement>("[data-copy]").forEach((b) => b.addEventListener("click", () => copyLink(b.dataset.copy!)));
    root.querySelectorAll<HTMLButtonElement>("[data-send]").forEach((b) => b.addEventListener("click", () => send([b.dataset.send!])));
    root.querySelector("#send-all")?.addEventListener("click", () => send());

    root.querySelector<HTMLFormElement>("#add-person")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const displayName = (root.querySelector<HTMLInputElement>("#i-display")!).value.trim();
      const email = (root.querySelector<HTMLInputElement>("#i-email")!).value.trim() || undefined;
      const teamId = group ? "" : (root.querySelector<HTMLSelectElement>("#i-team")!).value;
      await act("createPlayer", { gameId: selected, teamId, displayName, email });
    });

    // Drag-and-drop reassignment between teams.
    root.querySelectorAll<HTMLElement>(".person").forEach((li) => {
      li.addEventListener("dragstart", (e) => e.dataTransfer!.setData("text/player", li.dataset.player!));
    });
    root.querySelectorAll<HTMLElement>(".team-group.drop").forEach((box) => {
      box.addEventListener("dragover", (e) => { e.preventDefault(); box.classList.add("drag-over"); });
      box.addEventListener("dragleave", () => box.classList.remove("drag-over"));
      box.addEventListener("drop", (e) => {
        e.preventDefault(); box.classList.remove("drag-over");
        const playerId = e.dataTransfer!.getData("text/player");
        if (playerId) act("setPlayerTeam", { playerId, teamId: box.dataset.team });
      });
    });
  }

  refresh().catch((e) => { root.innerHTML = `<div class="login-error" style="padding:20px">${(e as Error).message}</div>`; });
}
